import { Suspense, useMemo } from "react";
import styles from './searchData.module.css';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import './slider.css'
import './sliderNavigation.css';
import 'swiper/css/navigation';

type StatDisplayProps = {
  heading: string,
  icon: string
}

const StatDisplay = ({ heading, icon }: StatDisplayProps) => {
  return (
    <div className={styles.stats}>
      <picture>
        <img alt={heading} loading="lazy" className="light" src={`/icons/${icon}`} width="100" height="100" />
      </picture>
      <p>{heading}</p>
    </div>
  )
}

type Props = {
  title: string,
  isGoogleSearch: boolean,
  searchType: string,
  total: number,
  rent_max: number, 
  size_max: string, 
  cats: {
    [key: string]: number
  }, 
  availability: object, 
  topAgent: {
    agent: string,
    amount: number
  },
  rent_minimum: number, 
  rent_medium: number, 
  rent_avg: string | null, 
  size_minimum: string, 
  name: string, 
  searchCriteria: string, 
  portfolio: {
    name: string,  
  },
  forSale: boolean, 
  price_avg: string, 
  price_medium: number, 
  price_max: number, 
  price_minimum: number
}

type newObject = {
  '@context': string,
  '@type': string,
  mainEntity: mainEntity[]
}

type mainEntity = {
  '@type': string,
  name: string,
  acceptedAnswer: {
    '@type': string,
    text: string
  }
}

const AtAGlance = ({ title = '', isGoogleSearch = false, searchType, total, rent_max, size_max, cats, availability, topAgent,
  rent_minimum, rent_medium, rent_avg, size_minimum, name = '', searchCriteria, portfolio,forSale, price_avg, price_medium, price_max, price_minimum }: Props) => {

  let locationName = title || name
  const availabilityWording = forSale ? 'for sale' : 'to rent';
  const faqObject = useMemo(() => {
    const newObject: newObject = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [],
    };

    if (searchCriteria !== 'PORTFOLIO') {
      newObject.mainEntity.push({
        "@type": "Question",
        name: `How many shops are available ${availabilityWording} ${isGoogleSearch ? '' : `in ${locationName}`}?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `There are ${total} available ${searchType === "SCHEME" ? "retail schemes" : `shops ${availabilityWording}`} ${isGoogleSearch ? '' : `in ${locationName}`

            }.`,
        },
      });
      if (rent_max !== null)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the most expensive shop to rent in ${locationName}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The most expensive shop for rent is listed at £${rent_max.toLocaleString()} pa.`,
          },
        });
      if (rent_minimum !== null && rent_minimum !== undefined)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the least expensive shop for rent in ${name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The least expensive shop for rent is listed at £${rent_minimum.toLocaleString()} pa.`,
          },
        });

      if (rent_avg !== null && rent_avg !== undefined)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the average price of a shop to rent in ${name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The average price of rent in ${name} is £${parseInt(rent_avg).toLocaleString()} pa.`,
          },
        });
      if (rent_medium !== null && rent_medium !== undefined)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the median price of a shop to rent in ${name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The median price of rent in ${name} is £${rent_medium.toLocaleString()} pa.`,
          },
        });


      if (price_max !== null && price_max !== undefined && forSale)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the most expensive shop for sale in ${locationName}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The most expensive shop for sale is listed at £${price_max.toLocaleString()}.`,
          },
        });
      if (price_minimum !== null && price_minimum !== undefined && forSale)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the least expensive shop for sale in ${name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The least expensive shop for sale is listed at £${price_minimum.toLocaleString()}.`,
          },
        });

      if (price_avg !== null && price_avg !== undefined && forSale)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the average price of a shop for sale in ${name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The average price of rent in ${name} is £${parseInt(price_avg).toLocaleString()}.`,
          },
        });
      if (price_medium !== null && price_medium !== undefined && forSale)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the median price of a shop for sale in ${name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The median price of a shop to buy in ${name} is £${price_medium.toLocaleString()}.`,
          },
        });


      if (size_max !== null)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the largest ${searchType === "SCHEME" ? "retail scheme" : `shop ${availabilityWording}`} in ${locationName}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The largest ${searchType === "SCHEME" ? "retail scheme" : "available unit"
              } measures ${parseInt(size_max).toLocaleString()} sq ft.`,
          },
        });
      if (size_minimum !== null && size_minimum !== undefined)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the smallest ${searchType === "SCHEME" ? "retail scheme" : `shop ${availabilityWording}`} in ${name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The smallest ${searchType === "SCHEME" ? "retail scheme" : "available unit"
              } measures ${parseInt(size_minimum).toLocaleString()} sq ft.`,
          },
        });
      if (cats["SC"] > 0)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `How many ${searchType === "SCHEME" ? "retail schemes" : `shops ${availabilityWording}`
            } are listed within the Shopping Centres category in ${locationName}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `There ${cats["SC"] === 1 ? "is" : "are"} ${cats["SC"]} ${cats["SC"] === 1 ? (searchType === "SCHEME" ? "scheme" : "shop") : searchType === "SCHEME" ? "schemes" : "shops"
              } ${availabilityWording} ${searchType === "SCHEME" ? "in the" : "within"} Shopping Centres${searchType === "SCHEME" ? " category" : ""}`,
          },
        });
      if (cats["RW"] > 0)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `How many ${searchType === "SCHEME" ? "retail schemes" : "units"} are available on a retail park in ${name
            }?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `There ${cats["RW"] === 1 ? "is" : "are"} ${cats["RW"]} ${cats["RW"] === 1 ? (searchType === "SCHEME" ? "scheme" : "shop") : searchType === "SCHEME" ? "schemes" : "shops"
              } ${availabilityWording} ${searchType === "SCHEME" ? "in the" : "within"} Retail Parks${searchType === "SCHEME" ? " category" : ""}.`,
          },
        });
      if (cats["LE"] > 0)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `How many ${searchType === "SCHEME" ? "retail schemes" : `shops ${availabilityWording}`
            } are listed within the Leisure category in ${locationName}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `There ${cats["LE"] === 1 ? "is" : "are"} ${cats["LE"]} ${cats["LE"] === 1 ? (searchType === "SCHEME" ? "scheme" : "shop") : searchType === "SCHEME" ? "schemes" : "shops"
              } ${availabilityWording} ${searchType === "SCHEME" ? "in the" : "within"} Leisure Schemes${searchType === "SCHEME" ? " category" : ""}.`,
          },
        });
      if (cats["HS"] > 0)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `How many results are in central ${locationName}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `There ${cats["HS"] === 1 ? "is" : "are"} ${cats["HS"]} ${cats["HS"] === 1 ? (searchType === "SCHEME" ? "scheme" : "shop") : searchType === "SCHEME" ? "schemes" : "shops"
              } ${availabilityWording} in central ${locationName}.`,
          },
        });
      if (topAgent)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `Which agent has the most listings in ${locationName}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `${topAgent.agent} act as agents on ${topAgent.amount} results, making them the top-listed agent in ${locationName}.`,
          },
        });
    } else {

      newObject.mainEntity.push({
        "@type": "Question",
        name: `How many shops are available ${availabilityWording} belonging to ${portfolio.name}?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `There are ${total} available ${searchType === "SCHEME" ? "retail schemes" : `shops ${availabilityWording}`} belonging to ${portfolio.name}.`,
        },
      });
      if (rent_max !== null)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the most expensive shop ${availabilityWording} belonging to ${portfolio.name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The most expensive shop ${availabilityWording} is listed at £${rent_max.toLocaleString()} pa.`,
          },
        });
      if (rent_minimum !== null && rent_minimum !== undefined)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the least expensive shop ${availabilityWording} belonging to ${portfolio.name}`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The least expensive shop ${availabilityWording} is listed at £${rent_minimum.toLocaleString()} pa.`,
          },
        });

      if (rent_avg !== null && rent_avg !== undefined)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the average price of a shop ${availabilityWording} belonging to ${portfolio.name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The average price of rent in ${name} is £${parseInt(rent_avg).toLocaleString()} pa.`,
          },
        });
      if (rent_medium !== null && rent_medium !== undefined)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the median price of a shop ${availabilityWording} belonging to ${portfolio.name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The median price of rent in ${name} is £${rent_medium.toLocaleString()} pa.`,
          },
        });
      if (size_max !== null)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the largest ${searchType === "SCHEME" ? "retail scheme" : `shop ${availabilityWording}`} belonging to ${portfolio.name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The largest ${searchType === "SCHEME" ? "retail scheme" : "available unit"
              } measures ${parseInt(size_max).toLocaleString()} sq ft.`,
          },
        });
      if (size_minimum !== null && size_minimum !== undefined)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `What is the smallest ${searchType === "SCHEME" ? "retail scheme" : `shop ${availabilityWording}`} belonging to ${portfolio.name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `The smallest ${searchType === "SCHEME" ? "retail scheme" : "available unit"
              } measures ${parseInt(size_minimum).toLocaleString()} sq ft.`,
          },
        });
      if (cats["SC"] > 0)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `How many ${searchType === "SCHEME" ? "retail schemes" : `shops ${availabilityWording}`
            } are listed within the Shopping Centres category belonging to ${portfolio.name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `There ${cats["SC"] === 1 ? "is" : "are"} ${cats["SC"]} ${cats["SC"] === 1 ? (searchType === "SCHEME" ? "scheme" : "shop") : searchType === "SCHEME" ? "schemes" : "shops"
              } ${availabilityWording} ${searchType === "SCHEME" ? "in the" : "within"} Shopping Centres${searchType === "SCHEME" ? " category" : ""}`,
          },
        });
      if (cats["RW"] > 0)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `How many ${searchType === "SCHEME" ? "retail schemes" : "units"} are available on a retail park belonging to ${portfolio.name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `There ${cats["RW"] === 1 ? "is" : "are"} ${cats["RW"]} ${cats["RW"] === 1 ? (searchType === "SCHEME" ? "scheme" : "shop") : searchType === "SCHEME" ? "schemes" : "shops"
              } ${availabilityWording} ${searchType === "SCHEME" ? "in the" : "within"} Retail Parks${searchType === "SCHEME" ? " category" : ""}.`,
          },
        });
      if (cats["LE"] > 0)
        newObject.mainEntity.push({
          "@type": "Question",
          name: `How many ${searchType === "SCHEME" ? "retail schemes" : `shops ${availabilityWording}`
            } are listed within the Leisure category belonging to ${portfolio.name}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `There ${cats["LE"] === 1 ? "is" : "are"} ${cats["LE"]} ${cats["LE"] === 1 ? (searchType === "SCHEME" ? "scheme" : "shop") : searchType === "SCHEME" ? "schemes" : "shops"
              } ${availabilityWording} ${searchType === "SCHEME" ? "in the" : "within"} Leisure Schemes${searchType === "SCHEME" ? " category" : ""}.`,
          },
        });
    }

    return newObject;
  }, [total, rent_max, size_max, cats, availability, topAgent, locationName, isGoogleSearch, forSale, price_avg, price_medium, price_max, price_minimum]);

  return (
    <Suspense>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqObject) }} />
      <section className={`${styles.section} ${styles.statsContainer}`} id="atGlance">
        <Swiper navigation={true} modules={[Navigation]} style={{
          width: "100%",
        }}
          slidesPerView={1}
          spaceBetween={10}
          pagination={{
            clickable: true,
          }}

          breakpoints={{
            // when window width is >= 480px (mobile)
            480: {
              slidesPerView: 1,
              spaceBetween: 0,
            },
            // when window width is >= 768px (tablet)
            768: {
              slidesPerView: 2,
              //  spaceBetween: 30,
            },
            // when window width is >= 1024px (desktop)
            1024: {
              slidesPerView: 5,
              // spaceBetween: 30,
            },
          }}
        >


          {availability?.['For Sale'] && availability['For Sale'] > 0 && (
            <SwiperSlide ><StatDisplay heading={`${availability['For Sale']} ${availability['For Sale'] === 1 ? 'shop' : 'shops'} for sale`} icon="for-sale.svg" /></SwiperSlide>

          )}

          {!forSale && availability?.['To Let'] && availability['To Let'] > 0 && (
            <SwiperSlide ><StatDisplay heading={`${availability['To Let']} available ${availability['To Let'] === 1 ? 'shop' : 'shops'} for rent`} icon="for-rent.svg" /></SwiperSlide>

          )}

          {cats?.SC && cats.SC > 0 && (
            <SwiperSlide ><StatDisplay heading={`${cats.SC} available shopping centre ${cats.SC === 1 ? 'unit' : 'units'} `} icon="shopping-centre.svg" /></SwiperSlide>

          )}

          {cats?.RW && cats.RW > 0 && (
            <SwiperSlide ><StatDisplay heading={`${cats.RW} available retail park  ${cats.RW === 1 ? 'unit' : 'units'}`} icon="retail-park.svg" /></SwiperSlide>

          )}
          {cats?.LE && cats.LE > 0 && (
            <SwiperSlide ><StatDisplay heading={`${cats.LE} available lesiure park  ${cats.LE === 1 ? 'unit' : 'units'}`} icon="leisure-park.svg" /></SwiperSlide>

          )}
          {rent_avg && parseInt(rent_avg) > 0 && (
            <SwiperSlide > <StatDisplay heading={`Average annual rent £${parseInt(rent_avg).toLocaleString()}`} icon="rent.svg" /> </SwiperSlide>

          )}
          {price_avg && parseInt(price_avg) > 0 && (
            <SwiperSlide ><StatDisplay heading={`Average annual price £${parseInt(price_avg).toLocaleString()}`} icon="rent.svg" /></SwiperSlide>

          )}
          {topAgent && (
            <SwiperSlide > <StatDisplay heading={`Top listed agent ${topAgent.agent}`} icon="agent.svg" /></SwiperSlide>

          )}

        </Swiper>
      </section>
      {/*
      <section className={`${styles.section} ${styles.statsContainer}`}>
        {availability?.['For Sale'] &&  availability['For Sale'] > 0 && (
          <StatDisplay heading={`${availability['For Sale']} shops for sale`} icon="for-sale.svg"/>
        )}

        {!forSale && availability?.['To Let'] &&  availability['To Let'] > 0 && (
          <StatDisplay heading={`${availability['To Let']} available shops for rent`} icon="for-rent.svg" />
        )}

        {cats?.SC && cats.SC > 0 && (
          <StatDisplay heading={`${cats.SC} available shopping centre units`}  icon="shopping-centre.svg"/>
        )}

        {cats?.RW && cats.RW > 0 && (
          <StatDisplay heading={`${cats.RW} available retail park units`} icon="retail-park.svg"/>
        )}
        {cats?.LE && cats.LE > 0 && (
          <StatDisplay heading={`${cats.LE} available lesiure park units`} icon="leisure-park.svg" />
        )}
        {rent_avg && rent_avg > 0 && (
          <StatDisplay heading={`Average annual rent £${parseInt(rent_avg).toLocaleString()}`} icon="rent.svg" /> 
        )}
        {price_avg && price_avg > 0 && (
          <StatDisplay heading={`Average annual price £${parseInt(price_avg).toLocaleString()}`} icon="rent.svg" />
        )}
        {topAgent && (
          <StatDisplay heading={`Top listed agent ${topAgent.agent}`} icon="agent.svg" />
        )}
      </section>
      */}
    </Suspense>
  );
};

export default AtAGlance;
